import { useMemo, type ReactNode } from 'react';
import { useFormikContext } from 'formik';
import { Box } from '@chakra-ui/react';
import { ScorecardBuilder, type ScorecardErrors } from '@kula-ai/components';
import type { ScorecardType } from '@/types/interview';
import { uploadUserInlineImage } from '@/api/userImageUploads/useUploadUserInlineImage';
import {
  buildScorecardFillData,
  type ScorecardFillValues,
} from './scorecardFill';

interface FormFieldConfig {
  name: string | number;
  type?: string;
  [key: string]: unknown;
}

type AttachFn = (files: File[]) => void;
type RemoveFn = (record: unknown) => void;

const attachmentKeyOf = (record: any): string =>
  String(
    record?.id ??
      `${record?.name ?? record?.filename ?? ''}-${
        record?.size ?? record?.byte_size ?? ''
      }`
  );

/**
 * Shared FILL body: renders the kula-ai `ScorecardBuilder`. Every answer type —
 * including the rich `open_question` / `feedback` editors — renders natively;
 * the `onChange`s write back via Formik `setFieldValue`. Attachments reuse the
 * surface's existing upload/delete handlers (`getScoreCardFormConfig` binds
 * `onAttachmentChange` / `onAttachmentRemove` to the BE upload + form-value
 * updates), routed through the builder's `onQuestionAttach` / `onFeedbackAttach`.
 *
 * Validation is owned by the builder: the host runs the BE-matching check (see
 * `useScorecardValidation`) and passes `errors` + `scrollToErrorToken`; the
 * builder renders each error below its field and scrolls to the first one.
 */
export const ScorecardFillBody = ({
  scorecard,
  formConfig,
  ratingBanner = null,
  disabled = false,
  errors,
  scrollToErrorToken,
}: {
  scorecard: ScorecardType;
  formConfig: FormFieldConfig[];
  ratingBanner?: ReactNode;
  /** Read-only / view mode (collect-score-card shows answers before "Edit"). */
  disabled?: boolean;
  /** Field-level errors from the BE-matching check (`useScorecardValidation`). */
  errors?: ScorecardErrors;
  /** Bump on a blocked submit to scroll the first invalid field into view. */
  scrollToErrorToken?: number | string;
}) => {
  const formik = useFormikContext<ScorecardFillValues>();
  const { values, setFieldValue } = formik;

  // Memoised so the builder/native editor get stable prop identities across the
  // frequent Formik re-renders — a fresh `data`/`editorOptions` each render
  // re-initialises the tiptap editor and yanks scroll/focus to the top.
  const data = useMemo(
    () => buildScorecardFillData(values, scorecard, formConfig),
    [values, scorecard, formConfig]
  );

  // The feedback/rating sections are optional per surface — collect-score-card
  // omits the rating field from its config in some states. Mirror that here.
  const showFeedback = formConfig.some((f) => String(f.name) === 'feedback');
  const showRating = formConfig.some((f) => String(f.name) === 'rating');

  const getFieldConfig = (name: string) =>
    formConfig.find((field) => String(field.name) === name);

  // The attachment knobs (accept list, size cap) are the same across editor
  // fields — read them off whichever field carries them.
  const editorConfig = formConfig.find((f) => f.attachmentFiles !== undefined);

  // Stable across renders — matches the legacy FormEditor toolbar (no
  // alignment / font size / family) and a taller answer area. Recreating this
  // object each render reconfigures the editor and resets scroll.
  const editorOptions = useMemo(
    () => ({
      questionPlaceholder: 'Answer here',
      feedbackPlaceholder: 'Write your feedback about the candidate here',
      attachmentAccept: editorConfig?.attachmentFiles as string | undefined,
      attachmentMaxFileSizeMb: editorConfig?.totalSizeLimitMB as
        | number
        | undefined,
      minHeight: '12rem',
      // Upload pasted/inserted images to a URL (POST /user_image_uploads) like
      // the old editor, instead of bloating the saved HTML with base64.
      uploadInlineImage: uploadUserInlineImage,
      toolbarFeatures: {
        bold: true,
        italic: true,
        underline: true,
        strike: true,
        textStyle: true,
        link: true,
        list: true,
        image: true,
        clearFormat: true,
        attachment: true,
        align: false,
        fontSize: false,
        fontFamily: false,
        code: false,
        overflow: false,
      },
    }),
    [editorConfig?.attachmentFiles, editorConfig?.totalSizeLimitMB]
  );

  const attach = (config: FormFieldConfig | undefined, files: File[]) =>
    (config?.onAttachmentChange as AttachFn | undefined)?.(files);

  const removeAttachment = (
    config: FormFieldConfig | undefined,
    records: unknown,
    key: string
  ) => {
    const list = Array.isArray(records) ? records : [];
    const record = list.find((r) => attachmentKeyOf(r) === key);
    (config?.onAttachmentRemove as RemoveFn | undefined)?.(
      record ?? { id: key }
    );
  };

  return (
    <ScorecardBuilder
      data={data}
      disabled={disabled}
      showFeedback={showFeedback}
      showRating={showRating}
      errors={errors}
      scrollToErrorToken={scrollToErrorToken}
      editorOptions={editorOptions}
      onQuestionChange={(index, value) =>
        setFieldValue(String(index + 1), value)
      }
      onFeedbackChange={(value) => setFieldValue('feedback', value)}
      onRatingChange={(value) => setFieldValue('rating', value)}
      onQuestionAttach={(index, files) =>
        attach(getFieldConfig(String(index + 1)), files)
      }
      onQuestionRemoveAttachment={(index, key) =>
        removeAttachment(
          getFieldConfig(String(index + 1)),
          values[`${index + 1}_attachments`],
          key
        )
      }
      onFeedbackAttach={(files) => attach(getFieldConfig('feedback'), files)}
      onFeedbackRemoveAttachment={(key) =>
        removeAttachment(
          getFieldConfig('feedback'),
          values.feedback_attachments,
          key
        )
      }
      renderRating={({ renderDefault }) => (
        <Box id="rating">
          {ratingBanner}
          {renderDefault()}
        </Box>
      )}
    />
  );
};
